import makeStyles from '@mui/styles/makeStyles';
import { Box, Link, Stack, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { CompanyFile, EmailBody } from '../../../types/files';
import EventTypeLabel from '../../atoms/dashboard-details-v2/EventTypeLabel';
import EmailMetaText from '../../atoms/dashboard-details-v2/EmailMetaText';
import DocumentsSection from './DocumentsSection';

export type EmailWithDocs = EmailBody & { attached: CompanyFile[]; linked: string[] };

type TimelineEntryProps = {
    email: EmailWithDocs;
    subject?: string;
    metaText: string;
    allFiles: CompanyFile[];
    filter: string;
    showViewEmail?: boolean;
    onFileClick: (file: CompanyFile) => void;
    onViewEmailClick: () => void;
};

const useStyles = makeStyles((theme) => ({
    dateColumn: {
        width: 120,
        textAlign: 'right',
        paddingRight: theme.spacing(3),
        borderRight: '2px solid #e9ecef',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            right: -7,
            top: 4,
            width: 12,
            height: 12,
            backgroundColor: '#1a8c8c',
            borderRadius: '50%',
        },
    },
    dateText: {
        fontSize: 13,
        color: '#6c757d',
        fontWeight: 500,
    },
    contentColumn: {
        flex: 1,
        paddingLeft: theme.spacing(1),
    },
    subject: {
        fontSize: 15,
        fontWeight: 600,
        color: '#2c3e50',
        marginBottom: theme.spacing(2),
    },
    viewEmailLink: {
        marginTop: theme.spacing(1.5),
        display: 'inline-block',
        color: '#1a8c8c',
        textDecoration: 'none',
        fontSize: 13,
        fontWeight: 500,
        '&:hover': {
            textDecoration: 'underline',
            color: '#167676',
        },
    },
}));

const TimelineEntry: React.FC<TimelineEntryProps> = ({
    email, subject, metaText, allFiles, filter, showViewEmail = true, onFileClick, onViewEmailClick,
}) => {
    const classes = useStyles();
    const showDocuments = (!!email.attached?.length || !!email.linked?.length) && (filter === 'All' || filter === 'Documents');

    return (
        <Stack direction="row" spacing={3}>
            <Box className={classes.dateColumn}>
                <Typography className={classes.dateText}>
                    {moment(email.createdAt).format('MMM D, YYYY')}
                </Typography>
            </Box>
            <Box className={classes.contentColumn}>
                <EventTypeLabel type={email.contentClassification ?? ''} />
                <Typography className={classes.subject}>{subject ?? email.subject}</Typography>
                <Stack spacing={2}>
                    {showDocuments && (
                        <DocumentsSection
                            files={email.attached ?? []}
                            links={email.linked ?? []}
                            allFiles={allFiles}
                            onFileClick={onFileClick}
                        />
                    )}
                </Stack>
                <EmailMetaText text={metaText} />
                {showViewEmail && (
                    <Link href="#" className={classes.viewEmailLink} onClick={onViewEmailClick}>
                        View full email →
                    </Link>
                )}
            </Box>
        </Stack>
    );
};

export default TimelineEntry;
